import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import axios from "axios";

import { API_ENV, API_HOST, ADMIN_API_KEY } from "../../../constants";

import "./styles/index.css";
import ApiRequestInfoModal, { getStatusColor } from "./components/ApiRequestsInfoModal";

const getApiRequests = async (apiEnv = API_ENV.DEV, params = {}) => {
  const response = await axios.get(
    `${API_HOST[apiEnv]}/v1/requests`,
    {
      headers: { "X-API-KEY": ADMIN_API_KEY[apiEnv] },
      params
    }
  );

  return response.data;
};

const DEFAULT_PAGE_SIZE = 10;
const INITIAL_PAGE = 1;

const useGetApiRequests = ({ apiEnv, apiKey, responseStatus, onlyErrors }) => {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);

  const setPageSizeHandler = (value) => {
    const numberValue = parseInt(value);
    if (numberValue > 0) {
      setPageSize(numberValue);
    }
  }

  const setCurrentPageHandler = (value) => {
    const numberValue = parseInt(value);
    if (numberValue > 0) {
      setCurrentPage(numberValue);
    }
  }

  const params = {
    ...(apiKey && { apiKey }),
    // ...(pageSize && { limit: pageSize }),
    // ...(currentPage && { page: currentPage }),
    ...(responseStatus && { responseStatus }),
    ...(onlyErrors && { onlyErrors })
  }

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: ["prdApiRequests", apiEnv, params],
    queryFn: async () => await getApiRequests(apiEnv, params ),
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  // data
  const apiRequests = data?.data ?? [];
  const totalRequests = apiRequests.length;
  const initialRequestDate = (new Date(apiRequests[apiRequests.length - 1]?.createdAt)).toLocaleString();
  const lastRequestDate = (new Date(apiRequests[0]?.createdAt)).toLocaleString();

  // pagination
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const lastAvailablePage = Math.ceil(apiRequests.length / pageSize);
  const paginatedApiRequests = apiRequests.slice(startIndex, endIndex);

  return {
    apiRequests: paginatedApiRequests,
    initialRequestDate,
    lastRequestDate,
    totalRequests,
    isError,
    isLoading,
    refetchApiRequests: refetch,
    // pagination
    pagination: {
      currentPage,
      lastAvailablePage,
      pageSize,
      setPageSizeHandler,
      setCurrentPageHandler
    }
  };
};

const ApiRequest = ({ apiRequest, onClick }) => (
  <div className="api-card" onClick={onClick}>
    <h5>{apiRequest.id}</h5>

    <p><b>{apiRequest.requestMethod}</b></p>
    <p>Endpoint: {apiRequest.requestedEndpoint}</p>
    <p>
      {"Response Status: "}
      <b className={getStatusColor(apiRequest.responseStatus)}>
        {apiRequest.responseStatus}
      </b>
    </p>
    
    <p>Date: {(new Date(apiRequest.createdAt)).toLocaleString()}</p>
  </div>
);

const ApiRequestsPage = () => {
  const { environment } = useSelector((state) => state.config);
  const { search: searchQuery } = useLocation();

  // Modals
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [selectedApiRequest, setSelectedApiRequest] = useState("");

  // Filters
  const [apiKeyFilter, setApiKeyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [onlyErrorsFilter, setOnlyErrorsFilter] = useState(false);

  const {
    apiRequests,
    initialRequestDate,
    lastRequestDate,
    totalRequests,
    isError,
    isLoading,
    pagination
  } = useGetApiRequests({
    apiEnv: environment,
    apiKey: apiKeyFilter,
    responseStatus: statusFilter || undefined,
    onlyErrors: onlyErrorsFilter
  });

  // Effect to update the apiKeyFilter state when redirecting from the ApiKeysPage
  useEffect(() => {
    if (!searchQuery) return;

    const params = new URLSearchParams(searchQuery);
    setApiKeyFilter(params.get("api_key") || "");
  }, [searchQuery]);

  // Filters handlers
  const onApiKeyFilterChange = ({target: { value }}) => {
    const trimmedValue = value?.trim();
    if (!trimmedValue || trimmedValue === "") {
      setApiKeyFilter("");;
    } else {
      setApiKeyFilter(trimmedValue);
    }
  }

  const onStatusFilterChange = ({target: { value }}) => {
    const status = parseInt(value) || 0;
    if (status === 0) {
      setStatusFilter("");
    } else {
      setStatusFilter(status);
    }
  };

  const onOnlyErrorsFilterChange = ({target: { checked }}) => {
    setOnlyErrorsFilter(checked);
  };

  // Modal handlers
  const handleApiRequestClick = (apiRequest) => {
    setSelectedApiRequest(apiRequest);
    setIsInfoModalOpen(true);
  };

  // Render
  const filters = (
    <div id="api-requests-filters">
      <label>
        {"Filter by API Key: "}
        <input type="text" value={apiKeyFilter} onChange={onApiKeyFilterChange}/>
      </label>
      <div className="filter-row">
        <label>
          {"Filter by Status Code: "}
          <input id="status-filter" type="text" value={statusFilter} onChange={onStatusFilterChange}/>
        </label>
        <label>
          {"Only Errors: "}
          <input id="error-filter" type="checkbox" value={onlyErrorsFilter} onChange={onOnlyErrorsFilterChange}/>
        </label>
      </div>
    </div>
  );



  return (
    <section className="page">
      <h1>{environment} API Requests</h1>

      {filters}
      <PaginationControls pagination={pagination} />

      {isLoading && <p>Loading...</p>}
      {isError && <p>Error loading API requests</p>}
      {!apiRequests.length && !isLoading && !isError && <p>No API Requests found</p>}

      {!!apiRequests.length &&
        <div id="api-requests-analytics">
          <p><b>{"Requests: "}</b> {pagination.pageSize * pagination.currentPage} / {totalRequests} </p>
        </div>
      }

      {apiRequests.map((apiRequest) =>
        <ApiRequest
          key={apiRequest.id}
          apiRequest={apiRequest}
          onClick={() => handleApiRequestClick(apiRequest)}
        />
      )}

      {isInfoModalOpen && 
        <ApiRequestInfoModal
          onCloseClick={() => setIsInfoModalOpen(false)}
          apiRequest={selectedApiRequest}
        />
      }
    </section>
  )
};

const PaginationControls = ({pagination}) => (
  <div id="api-requests-pagination">
    <button
      className="btn terciary btn--small"
      disabled={pagination.currentPage <= 1}
      onClick={() => pagination.setCurrentPageHandler(pagination.currentPage - 1)}
    >
      {"<<"}
    </button>
    <label>
      {"Page: "}
      <input
        className="nav-input"
        type="number"
        value={pagination.currentPage}
        onChange={({target: {value}}) => pagination.setCurrentPageHandler(value)}
      />
    </label>
    <label>
      {"Page Size: "}
      <input
        className="nav-input"
        type="number"
        value={pagination.pageSize}
        onChange={({target: {value}}) => pagination.setPageSizeHandler(value)}
      />
    </label>
    <button
      className="btn terciary btn--small"
      disabled={pagination.currentPage >= pagination.lastAvailablePage}
      onClick={() => pagination.setCurrentPageHandler(pagination.currentPage + 1)}
    >
      {">>"}
    </button>
  </div>
);

export default ApiRequestsPage;
